import classNames from "classnames";
import { SearchInput } from "components";
import { AssetVisibilityConfirmationModal } from "components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";
import { type CardsViewerItem } from "components/cardsViewer/types";
import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import {
  ContentTypesEnum,
  ItemsTypes,
  RolePermissions,
  RouteNames,
  SortingDirection,
  type SortOptions,
  ViewType,
} from "enums";
import SortDirections from "enums/sortDirections";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useHasPermissions } from "features/Application/hooks";
import {
  EmbeddedVideoPublishSuccess,
  LockEmbeddedVideoSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { useObservable } from "hooks/useObservable";
import { useRtn } from "hooks/useRtn";
import { bindAction } from "interfaces";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { isEmpty, noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { useNavigate } from "react-router";
import { type Filters } from "utils/queryUtils";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import {
  resetAppliedFilter,
  resetPagination,
  resetSearch,
  resetSortingColumnName,
  resetSortingDirection,
  setAppliedFilter,
  setPlainSortingDirection,
  setSearch,
  setSortingColumnName,
  setSortingDirection,
} from "../state/EmbeddedVideosFiltersSlice";
import { reset, setSelected } from "../state/EmbeddedVideosOverviewSlice";
import { lockEmbeddedVideo } from "../state/thunks/EmbeddedVideosEntityStateThunk";
import { getOverviewFilterOptions } from "../state/thunks/EmbeddedVideosFiltersThunk";
import {
  changeEmbeddedVideosVisibility,
  deleteEmbeddedVideos,
  duplicateEmbeddedVideos,
  fetchEmbeddedVideos,
} from "../state/thunks/EmbeddedVideosOverviewThunk";
import { type EmbeddedVideoOverview } from "../types/models";
import { Columns, columnToParamMap, getColumnOptions, sortOptions } from "./columnOptions";
import { EmbeddedVideoOverviewCard } from "./EmbeddedVideoOverviewCard";
import { EmbeddedVideosFilterForm } from "./EmbeddedVideosFilterForm";
import { EmbeddedVideoNoResults } from "./EmbeddedVideosNoResults";
import { EmbeddedVideosOverviewHeader } from "./EmbeddedVideosOverviewHeader";

const DEFAULT_FETCH_GRID_TOP = Number.parseInt(process.env.REACT_APP_LOAD_ITEMS_COUNT as string) || 30;
const DEFAULT_SORTBY = "dateCreated";

export type EmbeddedVideosOverviewProps = PropsFromRedux & {
  renderContentSwitcher?: () => React.ReactElement;
};

export const EmbeddedVideosOverview = (props: EmbeddedVideosOverviewProps) => {
  const {
    items,
    itemsCount,
    isLoading,
    areAllLoaded,
    selected,
    filters,
    fetchEmbeddedVideos,
    deleteEmbeddedVideos,
    duplicateEmbeddedVideos,
    changeEmbeddedVideosVisibility,
    lockEmbeddedVideo,
    setSelectedEmbeddedVideos,
    getFilterOptions,
    applyFilter,
    resetAppliedFilter,
    resetSearchFilters,
    resetEmbeddedVideos,
    onSearchChanged,
    setListSortingColumnName,
    setListSortingDirection,
    setGridSortingDirection,
  } = props;

  const hasCreatePermission = useHasPermissions([RolePermissions.AssetsCreate]);
  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const [purchasedSelectedCount, setPurchasedSelectedCount] = useState(0);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [visibleSelectedCount, setVisibleSelectedCount] = useState(0);
  const [draftSelectedCount, setDraftSelectedCount] = useState(0);
  const [selectedViewType, setSelectedViewType] = useState(ViewType.GRID);
  const isGridView = useMemo(() => selectedViewType === ViewType.GRID, [selectedViewType]);

  const deleteObserver =
    useObservable<(onConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>();
  const changeVisibilityObserver = useObservable<(onConfirm: () => void, hidden: boolean, ids: number[]) => void>();

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      resetEmbeddedVideos();
      if (!window.location.pathname.includes(`/${RouteNames.embeddedVideos}`)) {
        resetSearchFilters();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToEditEntity = (payload: { id: number }) => navigate(`${payload.id}/configuration`);
  useRtn(LockEmbeddedVideoSuccess, goToEditEntity);

  const onSelectionChanged = (args: SelectionChangedArgs<EmbeddedVideoOverview>) => {
    let purchasedCount = 0;
    let draftCount = 0;
    let hiddenCount = 0;
    let visibleCount = 0;

    const onAdded = (video: EmbeddedVideoOverview) => {
      video.isPurchased && ++purchasedCount;
      video.isDraft && ++draftCount;
      !video.visibility && ++hiddenCount;
      video.visibility && ++visibleCount;
    };
    const onRemoved = (video: EmbeddedVideoOverview) => {
      video.isPurchased && --purchasedCount;
      video.isDraft && --draftCount;
      !video.visibility && --hiddenCount;
      video.visibility && --visibleCount;
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, clearSelection);

    setPurchasedSelectedCount(purchasedSelectedCount + purchasedCount);
    setDraftSelectedCount(draftSelectedCount + draftCount);
    setHiddenSelectedCount(hiddenSelectedCount + hiddenCount);
    setVisibleSelectedCount(visibleSelectedCount + visibleCount);
  };

  const onViewTypeChange = (viewType: ViewType) => {
    resetEmbeddedVideos();
    clearSelection();
    setSelectedViewType(viewType);
    if (viewType === ViewType.GRID) {
      loadCards(0);
    }
  };

  const clearSelection = useCallback(() => {
    setSelectedEmbeddedVideos([]);
    setPurchasedSelectedCount(0);
    setHiddenSelectedCount(0);
    setVisibleSelectedCount(0);
    setDraftSelectedCount(0);
  }, [setSelectedEmbeddedVideos]);

  const handleSearchChanged = (search: string) => {
    onSearchChanged(search);
    refetchItemsFromStart();
  };

  const handleFetchEmbeddedVideos = (
    skip?: number,
    top?: number,
    sortingColumnName?: string,
    sortDirection?: SortingDirection,
  ) => {
    isGridView
      ? loadCards(items.length, sortingColumnName, sortDirection)
      : loadList(skip, top, sortingColumnName, sortDirection);
  };

  const loadList = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = Columns.Added,
      sortingDirection: SortingDirection = SortingDirection.Descending,
    ) => {
      const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<Columns>];
      fetchEmbeddedVideos(skip, top, sortBy, sortingDirection);
    },
    [fetchEmbeddedVideos],
  );

  const loadCards = useCallback(
    (skip: number, sortBy: string = DEFAULT_SORTBY, sortDirection: string = SortDirections.Desc) => {
      fetchEmbeddedVideos(skip, DEFAULT_FETCH_GRID_TOP, sortBy, sortDirection, true);
    },
    [fetchEmbeddedVideos],
  );

  const refetchItemsFromStart = useCallback(() => {
    if (isGridView) {
      resetEmbeddedVideos();
      loadCards(0, filters.sortingColumnName, filters.sortingDirection);
    } else {
      // @ts-ignore
      reloadListItemsRef.current?.(isEmpty(filters.search));
    }
  }, [resetEmbeddedVideos, isGridView, loadCards, filters.search, filters.sortingColumnName, filters.sortingDirection]);

  const handleEdit = useCallback(
    (id: number, isDraft: boolean) => {
      isDraft ? navigate(`${id}/configuration`) : lockEmbeddedVideo(id);
    },
    [navigate, lockEmbeddedVideo],
  );

  const handleDuplicate = useCallback(
    (ids: number[]) => {
      clearSelection();
      duplicateEmbeddedVideos(ids, refetchItemsFromStart);
    },
    [duplicateEmbeddedVideos, refetchItemsFromStart, clearSelection],
  );

  const handleDelete = useCallback(
    (ids: number[], flowsCount?: number, packsCount?: number) => {
      deleteObserver.notify(
        () => {
          deleteEmbeddedVideos(ids, refetchItemsFromStart);
          clearSelection();
        },
        {
          selectedItemsCount: ids.length,
          flowsCount,
          packsCount,
        },
      );
    },
    [deleteObserver, deleteEmbeddedVideos, refetchItemsFromStart, clearSelection],
  );

  const handleToggleVisibility = useCallback(
    (ids: number[], hidden: boolean) => () => {
      changeVisibilityObserver.notify(
        () => {
          changeEmbeddedVideosVisibility(ids, hidden, refetchItemsFromStart);
          clearSelection();
        },
        hidden,
        ids,
      );
    },
    [changeVisibilityObserver, changeEmbeddedVideosVisibility, refetchItemsFromStart, clearSelection],
  );

  const columnOptions = useMemo(() => {
    return getColumnOptions({
      readonly: !hasCreatePermission,
      hideDisabled: !hasCreatePermission,
      handleEdit,
      handleDuplicate,
      handleDelete,
      handleToggleVisibility,
    });
  }, [handleDelete, handleToggleVisibility, handleEdit, handleDuplicate, hasCreatePermission]);

  const onGridSortChange = async (_: React.SyntheticEvent<HTMLElement>, data: { value?: SortOptions }) => {
    resetEmbeddedVideos();
    clearSelection();

    const [sortBy, sortDirection] = data.value!.split(" ");
    setSortingColumnName(sortBy);
    setGridSortingDirection(sortDirection);
    loadCards(0, sortBy, sortDirection);
  };

  const applyNewFilter = useCallback(
    (newFilter: Filters) => {
      clearSelection();
      applyFilter(newFilter);
      if (isGridView) {
        resetEmbeddedVideos();
        loadCards(0, filters.sortingColumnName, filters.sortingDirection);
      }
    },
    [
      applyFilter,
      loadCards,
      resetEmbeddedVideos,
      clearSelection,
      isGridView,
      filters.sortingColumnName,
      filters.sortingDirection,
    ],
  );

  const resetFilter = useCallback(() => {
    clearSelection();
    resetAppliedFilter();
    if (isGridView) {
      resetEmbeddedVideos();
      loadCards(0);
    }
  }, [resetAppliedFilter, resetEmbeddedVideos, isGridView, clearSelection, loadCards]);

  return (
    <section className={classNames("nested-content")}>
      <EmbeddedVideosOverviewHeader
        purchasedSelected={purchasedSelectedCount > 0}
        hiddenSelected={hiddenSelectedCount > 0}
        visibleSelected={visibleSelectedCount > 0}
        draftSelected={draftSelectedCount > 0}
        selectedIds={selected}
        clearSelection={clearSelection}
        handleDuplicate={handleDuplicate}
        handleDelete={handleDelete}
        handleToggleVisibility={handleToggleVisibility}
      />
      <GenericItemsView
        blur
        items={items}
        itemsType={ItemsTypes.Video}
        viewType={selectedViewType}
        isLoading={isLoading}
        columnOptions={columnOptions}
        fetchData={handleFetchEmbeddedVideos}
        dataCount={itemsCount}
        renderSearch={() => (
          <SearchInput
            placeholder="Search for Videos..."
            onChange={handleSearchChanged}
            defaultValue={filters.search}
          />
        )}
        selectedIds={selected}
        isSelectDisabled={() => !hasCreatePermission}
        onSelectionChanged={onSelectionChanged}
        onSelectedGridItemsChanged={setSelectedEmbeddedVideos}
        onSelectedItemChanged={setSelectedEmbeddedVideos}
        // @ts-ignore
        getFilterForm={() => <EmbeddedVideosFilterForm />}
        getFilterOptions={getFilterOptions}
        filterOptions={filters.filterOptions}
        filterOptionsLoading={filters.isLoading}
        applyFilter={applyNewFilter}
        resetFilter={resetFilter}
        setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
        appliedFilter={filters.appliedFilter}
        sortingDirection={filters.sortingDirection}
        sortOptions={sortOptions}
        onLoad={isGridView ? () => loadCards(0) : noop}
        onSortChange={onGridSortChange}
        setSortingColumnName={setListSortingColumnName}
        setSortingDirection={setListSortingDirection}
        defaultSortingColumnName={Columns.Added}
        onViewTypeChange={onViewTypeChange}
        isAllDataLoaded={areAllLoaded}
        listViewRtnEvents={[EmbeddedVideoPublishSuccess]}
        renderCard={(props: CardsViewerItem<EmbeddedVideoOverview>) => {
          return (
            <EmbeddedVideoOverviewCard
              {...props}
              handleEdit={handleEdit}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              handleToggleVisibility={handleToggleVisibility}
              readonly={!hasCreatePermission}
              hideDisabled={!hasCreatePermission}
            />
          );
        }}
        noResultsContent={
          <EmbeddedVideoNoResults
            filtered={!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)}
            withActionBtn
          />
        }
        permissions={[RolePermissions.AssetsManage]}
        customHeaderContent={props.renderContentSwitcher?.()}
      />
      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum["Embedded Videos"]}
        onTriggerRemoveContentObserver={deleteObserver}
      />
      <AssetVisibilityConfirmationModal observer={changeVisibilityObserver} assetType="embedded video" />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { overview, filters } = state.library.embeddedVideos;
  return {
    ...overview,
    filters,
  };
};
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetEmbeddedVideos: bindAction(reset, dispatch),
    fetchEmbeddedVideos: bindAction(fetchEmbeddedVideos, dispatch),
    deleteEmbeddedVideos: bindAction(deleteEmbeddedVideos, dispatch),
    duplicateEmbeddedVideos: bindAction(duplicateEmbeddedVideos, dispatch),
    changeEmbeddedVideosVisibility: bindAction(changeEmbeddedVideosVisibility, dispatch),
    lockEmbeddedVideo: bindAction(lockEmbeddedVideo, dispatch),
    getFilterOptions: bindAction(getOverviewFilterOptions, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    setSelectedEmbeddedVideos: bindAction(setSelected, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetAppliedFilter: bindAction(resetAppliedFilter, dispatch),
    setListSortingColumnName: bindAction(setSortingColumnName, dispatch),
    setListSortingDirection: bindAction(setSortingDirection, dispatch),
    setGridSortingDirection: bindAction(setPlainSortingDirection, dispatch),
    resetSearchFilters: () => {
      dispatch(resetSearch());
      dispatch(resetPagination());
      dispatch(resetAppliedFilter());
      dispatch(resetSortingColumnName());
      dispatch(resetSortingDirection());
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EmbeddedVideosOverview);
export default ConnectedComponent;
